import React, { useRef, useState } from "react";
import { updateUserProfile } from "../../../graphql/client";
import SignaturePad from "./SignaturePad";
import { WAIVER_TEXT, WAIVER_VERSION } from "./waiverContent";
// Reuse the rebuilt profile-modal look (.uim classes).
import "../UserInfoModal/UserInfoModal.css";

const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

// One-time waiver backfill for members who joined before the signup
// flow collected signatures — or whose signup got interrupted partway.
// Blocking by design (no close button); the dashboard renders it until
// a signature is on file.
//
// When `needSex` is true (the account has no male/female on record —
// e.g. a signup that was abandoned before the t-shirt step saved),
// the modal ALSO collects the fit question in the same screen, so
// signing here fully completes the account: a woman leaves this modal
// auto-approved with locations visible, not stuck in limbo.
//
// Saves through the authenticated updateUserProfile mutation, which
// stores the signature set-once, stamps signedAt server-side, and
// auto-approves females.
const WaiverModal = ({ defaultName, needSex, onSigned }) => {
  // Browser autofill / token fallbacks sometimes hand us the EMAIL as
  // the "name" — don't seed a legal-name field with that.
  const cleanDefault =
    defaultName && !defaultName.includes("@") ? defaultName : "";
  const [name, setName] = useState(cleanDefault);
  const [sex, setSex] = useState("");
  const [shirtSize, setShirtSize] = useState("M");
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if (needSex && sex !== "male" && sex !== "female") {
      setError("Please choose men's or women's fit.");
      return;
    }
    if (!name.trim() || name.includes("@")) {
      setError("Please enter your full name (not your email).");
      return;
    }
    if (!hasDrawn) {
      setError("Please sign with your finger or mouse to continue.");
      return;
    }
    let signatureDataUrl = "";
    try {
      signatureDataUrl = canvasRef.current.toDataURL("image/png");
    } catch (e) {
      console.warn("Could not export signature:", e);
    }
    setLoading(true);
    try {
      const payload = {
        waiverSignature: signatureDataUrl || null,
        waiverName: name.trim(),
        waiverVersion: WAIVER_VERSION,
      };
      if (needSex) {
        payload.sex = sex;
        payload.shirtSize = shirtSize;
      }
      await updateUserProfile(payload);
      onSigned(needSex ? { sex, shirtSize } : {});
    } catch (err) {
      setError(
        `Couldn't save your signature (${err.message || err}). Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-info-modal-overlay">
      <div className="user-info-modal uim">
        <div className="uim-header">
          <h3 className="uim-title">
            {needSex ? "Finish setting up" : "One quick signature"}
          </h3>
        </div>
        <div className="uim-body">
          <p className="uim-description">
            {needSex
              ? "Two quick things before you continue — your t-shirt fit and our liability waiver. You'll only see this once."
              : "Before you continue, please review and sign our liability waiver. You'll only see this once."}
          </p>

          {needSex && (
            <>
              <div className="uim-field">
                <label>T-shirt fit</label>
                <div className="uim-fit-buttons">
                  <button
                    type="button"
                    className={
                      sex === "female"
                        ? "uim-fit-btn uim-fit-btn--active"
                        : "uim-fit-btn"
                    }
                    onClick={() => setSex("female")}
                  >
                    Women's
                  </button>
                  <button
                    type="button"
                    className={
                      sex === "male"
                        ? "uim-fit-btn uim-fit-btn--active"
                        : "uim-fit-btn"
                    }
                    onClick={() => setSex("male")}
                  >
                    Men's
                  </button>
                </div>
              </div>
              <div className="uim-field">
                <label htmlFor="waiver-backfill-size">Shirt size</label>
                <select
                  id="waiver-backfill-size"
                  className="uim-input"
                  value={shirtSize}
                  onChange={(e) => setShirtSize(e.target.value)}
                >
                  {SHIRT_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div
            style={{
              maxHeight: needSex ? 140 : 180,
              overflowY: "auto",
              border: "1px solid #d0d6e0",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 12.5,
              color: "#3a4150",
              whiteSpace: "pre-wrap",
              marginBottom: 12,
              textAlign: "left",
            }}
          >
            {WAIVER_TEXT}
          </div>

          <div className="uim-field">
            <label htmlFor="waiver-backfill-name">Your full name</label>
            <input
              id="waiver-backfill-name"
              type="text"
              className="uim-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First and last name"
              autoComplete="name"
            />
          </div>

          <SignaturePad canvasRef={canvasRef} onDrawnChange={setHasDrawn} />

          {error && (
            <div className="uim-error" style={{ marginTop: 10 }}>
              {error}
            </div>
          )}
        </div>
        <div className="uim-footer">
          <button
            type="button"
            className="uim-btn uim-btn--primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving…" : "I agree — sign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaiverModal;
